/**
 * GuestStorageManager
 *
 * Provides IndexedDB-backed persistence for unauthenticated (guest) users.
 * All data is stored locally in the browser under the "argvoid-spritelab" database.
 *
 * Key format:  "project:sprite:sheet"   (mirrors sessionSheets Map keys)
 * Stored value: GuestSheetRecord
 */

export interface GuestSheetRecord {
    key: string;           // "project:sprite:sheet"
    base64: string;
    originalBase64: string;
    width: number;
    height: number;
    frames: number;
    animation: string;
    interval: number;
    palette: string[];
    savedAt: number;       // Date.now()
}

const DB_NAME = 'argvoid-spritelab';
const STORE = 'guestSheets';
const DB_VER = 1;

/** Cached connection — opened once, reused for the lifetime of the page. */
let _db: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
    if (_db) return Promise.resolve(_db);
    const t = performance.now();
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VER);
        req.onupgradeneeded = (e) => {
            const db = (e.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'key' });
            }
        };
        req.onsuccess = () => {
            _db = req.result;
            console.log(`[IDB] openDB took ${(performance.now() - t).toFixed(0)}ms`);
            resolve(_db);
        };
        req.onerror = () => reject(req.error);
    });
}

export const GuestStorageManager = {

    /** Fire-and-forget: opens the IDB connection in the background so the first real
     *  operation gets a cached handle instead of paying the 400–500ms open cost inline. */
    warmup() { openDB().catch(() => { }); },

    /** Persist a single sheet entry. */
    async saveSheet(record: GuestSheetRecord): Promise<void> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(record);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    },

    /** Load a single sheet entry, or null if not found. */
    async loadSheet(key: string): Promise<GuestSheetRecord | null> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).get(key);
            req.onsuccess = () => resolve(req.result ?? null);
            req.onerror = () => reject(req.error);
        });
    },

    /** Load all stored entries (full records including base64). Use sparingly — prefer getAllKeys() for metadata-only operations. */
    async loadAll(): Promise<GuestSheetRecord[]> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).getAll();
            req.onsuccess = () => resolve(req.result ?? []);
            req.onerror = () => reject(req.error);
        });
    },

    /** Fetch only the key strings from IDB — no image payloads. Fast path for tree/sheet enumeration. */
    async getAllKeys(): Promise<string[]> {
        const t = performance.now();
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).getAllKeys();
            req.onsuccess = () => {
                console.log(`[IDB] getAllKeys: ${req.result.length} keys in ${(performance.now() - t).toFixed(0)}ms`);
                resolve((req.result as string[]) ?? []);
            };
            req.onerror = () => reject(req.error);
        });
    },

    /** Delete a single entry. */
    async deleteSheet(key: string): Promise<void> {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    },

    /**
     * Derive the project-tree structure from all stored keys.
     * Returns: Array of { name: string, sprites: string[], createdAt: string, updatedAt: string }
     */
    async getProjectTree(): Promise<{ name: string; sprites: string[], createdAt?: string, updatedAt?: string }[]> {
        // Use keys-only scan — avoids loading base64 image payloads on startup
        const keys = await this.getAllKeys();
        const tree = new Map<string, Set<string>>();

        for (const key of keys) {
            const parts = key.split(':');
            if (parts.length < 3) continue;
            const [proj, sprite] = parts;
            if (!tree.has(proj)) tree.set(proj, new Set());
            tree.get(proj)!.add(sprite);
        }

        // Timestamps are derived lazily per-record only when needed; omit here for speed.
        const now = new Date().toISOString();
        return Array.from(tree.entries()).map(([name, sprites]) => ({
            name,
            sprites: Array.from(sprites),
            createdAt: now,
            updatedAt: now
        }));
    },

    /**
     * Return sheet names for a given project:sprite.
     */
    async getSheetsForSprite(projectId: string, spriteName: string): Promise<string[]> {
        // Use keys-only scan — no need to load image payloads just to get sheet names
        const keys = await this.getAllKeys();
        const prefix = `${projectId}:${spriteName}:`;
        return keys
            .filter(k => k.startsWith(prefix))
            .map(k => k.slice(prefix.length))
            .filter(Boolean);
    },

    /**
     * Rename a sheet in IDB: "project:sprite:oldSheet" → "project:sprite:newSheet"
     */
    async renameSheetKeys(projectId: string, spriteName: string, oldSheet: string, newSheet: string): Promise<void> {
        const oldKey = `${projectId}:${spriteName}:${oldSheet}`;
        const newKey = `${projectId}:${spriteName}:${newSheet}`;
        const record = await this.loadSheet(oldKey);
        if (!record) return;
        record.key = newKey;
        await this.saveSheet(record);
        await this.deleteSheet(oldKey);
    },

    /**
     * Rename a project in IDB: migrate all "oldName:..." keys to "newName:..."
     */
    async renameProjectKeys(oldName: string, newName: string): Promise<void> {
        const prefix = `${oldName}:`;
        const records = await this.loadAll();
        const toRename = records.filter(r => r.key.startsWith(prefix));
        for (const r of toRename) {
            const newKey = newName + ':' + r.key.slice(prefix.length);
            await this.deleteSheet(r.key);
            r.key = newKey;
            await this.saveSheet(r);
        }
    },

    /**
     * Rename a sprite in IDB: migrate all "project:oldSprite:..." keys to "project:newSprite:..."
     */
    async renameSpriteKeys(projectId: string, oldSprite: string, newSprite: string): Promise<void> {
        const prefix = `${projectId}:${oldSprite}:`;
        const records = await this.loadAll();
        const toRename = records.filter(r => r.key.startsWith(prefix));
        for (const r of toRename) {
            const remainder = r.key.slice(prefix.length);
            const newKey = `${projectId}:${newSprite}:${remainder}`;
            await this.deleteSheet(r.key);
            r.key = newKey;
            await this.saveSheet(r);
        }
    },

    /**
     * Delete a project from IDB: remove all records starting with "projectName:"
     */
    async deleteProjectKeys(projectName: string): Promise<void> {
        const prefix = `${projectName}:`;
        const records = await this.loadAll();
        const toDelete = records.filter(r => r.key.startsWith(prefix));
        for (const r of toDelete) {
            await this.deleteSheet(r.key);
        }
    },

    /**
     * Delete a sprite from IDB: remove all records starting with "project:sprite:"
     */
    async deleteSpriteKeys(projectId: string, spriteName: string): Promise<void> {
        const prefix = `${projectId}:${spriteName}:`;
        const records = await this.loadAll();
        const toDelete = records.filter(r => r.key.startsWith(prefix));
        for (const r of toDelete) {
            await this.deleteSheet(r.key);
        }
    },

    /**
     * Calculate approximate database size in Megabytes by stringifying all records.
     */
    async getDatabaseSizeInMB(): Promise<number> {
        try {
            const records = await this.loadAll();
            let totalBytes = 0;

            // Sum up the string length of each object. One char ~ 1 byte for base64 / english text.
            for (const r of records) {
                totalBytes += JSON.stringify(r).length;
            }

            return totalBytes / (1024 * 1024);
        } catch (e) {
            console.error('[IDB] Failed to calculate size', e);
            return 0;
        }
    }
};
